import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
} from "react";
import { useApp } from "@/contexts/AppContext";
import {
  LIGHT_THEME_PRESETS,
  DARK_THEME_PRESETS,
  RADIX_SWATCHES,
} from "@/styles/theme-presets";
import type { ThemeMode, ThemePalette } from "@/types/preferences";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Palette,
  Laptop,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  Sparkles,
  Clock,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

type Tone = "light" | "dark";

interface SwatchProps {
  palette: ThemePalette;
  active: boolean;
  onSelect: () => void;
  label: string;
  focusLabel: string;
  activeLabel: string;
}

type SwatchCardStyle = CSSProperties &
  Record<"--swatch-border" | "--swatch-border-hover" | "--swatch-shadow", string>;

const SwatchCard = ({
  palette,
  active,
  onSelect,
  label,
  focusLabel,
  activeLabel,
}: SwatchProps) => {
  const swatchStyle: SwatchCardStyle = active
    ? {
        borderColor: "var(--primary)",
        boxShadow: "0 0 0 3px var(--focus-ring)",
        "--swatch-border": "var(--primary)",
        "--swatch-border-hover": "var(--primary)",
        "--swatch-shadow": "var(--focus-ring)",
      }
    : {
        borderColor: "var(--swatch-border)",
        "--swatch-border": "transparent",
        "--swatch-border-hover": "color-mix(in srgb, var(--primary) 40%, var(--border))",
        "--swatch-shadow": "color-mix(in srgb, var(--primary) 24%, var(--background))",
      };

  return (
    <button
      onClick={onSelect}
      className="w-full text-left rounded-xl border transition-all overflow-hidden [--swatch-border:transparent] hover:[--swatch-border:var(--swatch-border-hover)] hover:shadow-[0_0_0_3px_var(--swatch-shadow)]"
      style={swatchStyle}
    >
    <div
      className="h-24 rounded-t-xl flex gap-2 p-3"
      style={{ background: palette.background }}
    >
      <div
        className="flex-1 rounded-lg border shadow-sm p-2 flex flex-col gap-1 justify-between"
        style={{
          background: palette.surface,
          borderColor: palette.foreground,
          color: palette.foreground,
        }}
      >
        <span
          className="h-2.5 rounded-full"
          style={{ backgroundColor: palette.primary }}
        />
        <span
          className="h-2.5 rounded-full"
          style={{ backgroundColor: palette.accent }}
        />
        <span
          className="h-2.5 rounded-full"
          style={{ backgroundColor: palette.foreground }}
        />
      </div>
      <div
        className="w-12 rounded-lg border shadow-sm flex flex-col justify-center gap-1 p-2"
        style={{ background: palette.surface, borderColor: palette.foreground }}
      >
        <span
          className="h-6 rounded"
          style={{ backgroundColor: palette.primary }}
        />
        <span
          className="h-3 rounded"
          style={{ backgroundColor: palette.accent }}
        />
      </div>
    </div>
    <div
      className="p-3 rounded-b-xl backdrop-blur"
      style={{
        backgroundColor:
          "color-mix(in srgb, var(--surface) 70%, var(--background))",
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <p
            className="text-sm font-medium"
            style={{
              color:
                "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
            }}
          >
            {label}
          </p>
          <p
            className="text-xs"
            style={{
              color:
                "color-mix(in srgb, var(--foreground) 78%, transparent)",
            }}
          >
            {focusLabel}
          </p>
        </div>
        {active && (
          <Badge variant="secondary" className="text-xs">
            {activeLabel}
          </Badge>
        )}
      </div>
    </div>
    </button>
  );
};

const pad2 = (value: number) => String(value).padStart(2, "0");
const WINDOW_OFFSETS = [-2, -1, 0, 1, 2];
const clampInt = (value: number, max: number) =>
  Math.max(0, Math.min(max, Math.round(value)));
const wrapValue = (value: number, max: number) =>
  (value + max + 1) % (max + 1);

const parseTimeValue = (value: string) => {
  const [rawHour = "0", rawMinute = "0"] = value.split(":");
  const hour = clampInt(Number(rawHour), 23);
  const minute = clampInt(Number(rawMinute), 59);
  return { hour, minute };
};

const TimeSlider = ({
  label,
  hourLabel,
  minuteLabel,
  value,
  onChange,
}: {
  label: string;
  hourLabel: string;
  minuteLabel: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  const { hour, minute } = parseTimeValue(value);
  const formatted = `${pad2(hour)}:${pad2(minute)}`;
  const hourRef = useRef(hour);
  const minuteRef = useRef(minute);
  const onChangeRef = useRef(onChange);
  const hourWheelCleanupRef = useRef<(() => void) | null>(null);
  const minuteWheelCleanupRef = useRef<(() => void) | null>(null);
  const handleHourSelect = (nextHour: string) => {
    onChange(`${nextHour}:${pad2(minute)}`);
  };
  const handleMinuteSelect = (nextMinute: string) => {
    onChange(`${pad2(hour)}:${nextMinute}`);
  };
  const handleHourInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const cleaned = event.target.value.replace(/\D/g, "");
    const nextHour = clampInt(Number(cleaned.slice(-2) || 0), 23);
    onChange(`${pad2(nextHour)}:${pad2(minute)}`);
  };
  const handleMinuteInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const cleaned = event.target.value.replace(/\D/g, "");
    const nextMinute = clampInt(Number(cleaned.slice(-2) || 0), 59);
    onChange(`${pad2(hour)}:${pad2(nextMinute)}`);
  };
  const hourWindow = WINDOW_OFFSETS.map((offset) =>
    pad2(wrapValue(hour + offset, 23))
  );
  const minuteWindow = WINDOW_OFFSETS.map((offset) =>
    pad2(wrapValue(minute + offset, 59))
  );

  useEffect(() => {
    hourRef.current = hour;
    minuteRef.current = minute;
    onChangeRef.current = onChange;
  }, [hour, minute, onChange]);

  const setHourWheelRef = useCallback((node: HTMLDivElement | null) => {
    if (hourWheelCleanupRef.current) {
      hourWheelCleanupRef.current();
      hourWheelCleanupRef.current = null;
    }
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.deltaY === 0) return;
      const step = event.deltaY > 0 ? 1 : -1;
      const nextHour = wrapValue(hourRef.current + step, 23);
      onChangeRef.current(
        `${pad2(nextHour)}:${pad2(minuteRef.current)}`
      );
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    hourWheelCleanupRef.current = () =>
      node.removeEventListener("wheel", onWheel);
  }, []);

  const setMinuteWheelRef = useCallback((node: HTMLDivElement | null) => {
    if (minuteWheelCleanupRef.current) {
      minuteWheelCleanupRef.current();
      minuteWheelCleanupRef.current = null;
    }
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.deltaY === 0) return;
      const step = event.deltaY > 0 ? 1 : -1;
      const nextMinute = wrapValue(minuteRef.current + step, 59);
      onChangeRef.current(
        `${pad2(hourRef.current)}:${pad2(nextMinute)}`
      );
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    minuteWheelCleanupRef.current = () =>
      node.removeEventListener("wheel", onWheel);
  }, []);
  return (
    <label className="flex flex-col gap-1 text-sm text-[var(--foreground)]">
      {label}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-10 w-full items-center justify-between rounded border border-border bg-input-background px-3 text-foreground transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          >
            <span className="font-medium">{formatted}</span>
            <Clock className="h-4 w-4 text-secondary" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2" ref={setHourWheelRef}>
              <input
                type="text"
                inputMode="numeric"
                value={pad2(hour)}
                onChange={handleHourInputChange}
                aria-label={hourLabel}
                className="h-9 w-full rounded border border-border bg-input-background text-center text-sm font-medium text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              />
              <div className="text-center text-sm font-semibold text-secondary">
                {hourLabel}
              </div>
              <div className="relative rounded border border-border bg-input-background pt-6 pb-6">
                <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center text-secondary opacity-80">
                  <ChevronUp className="mt-1 h-4 w-4" aria-hidden="true" />
                </div>
                {hourWindow.map((hourValue) => {
                  const isActive = hourValue === pad2(hour);
                  return (
                    <button
                      key={hourValue}
                      type="button"
                      tabIndex={-1}
                      onClick={() => handleHourSelect(hourValue)}
                      className="w-full px-2 py-1 text-sm transition-colors"
                      style={
                        isActive
                          ? {
                              background: "var(--primary-soft)",
                              color: "var(--foreground)",
                              fontWeight: 600,
                            }
                          : undefined
                      }
                    >
                      {hourValue}
                    </button>
                  );
                })}
                <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center text-secondary opacity-80">
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </div>
              </div>
            </div>
            <div className="space-y-2" ref={setMinuteWheelRef}>
              <input
                type="text"
                inputMode="numeric"
                value={pad2(minute)}
                onChange={handleMinuteInputChange}
                aria-label={minuteLabel}
                className="h-9 w-full rounded border border-border bg-input-background text-center text-sm font-medium text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              />
              <div className="text-center text-sm font-semibold text-secondary">
                {minuteLabel}
              </div>
              <div className="relative rounded border border-border bg-input-background pt-6 pb-6">
                <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center text-secondary opacity-80">
                  <ChevronUp className="mt-1 h-4 w-4" aria-hidden="true" />
                </div>
                {minuteWindow.map((minuteValue) => {
                  const isActive = minuteValue === pad2(minute);
                  return (
                    <button
                      key={minuteValue}
                      type="button"
                      tabIndex={-1}
                      onClick={() => handleMinuteSelect(minuteValue)}
                      className="w-full px-2 py-1 text-sm transition-colors"
                      style={
                        isActive
                          ? {
                              background: "var(--primary-soft)",
                              color: "var(--foreground)",
                              fontWeight: 600,
                            }
                          : undefined
                      }
                    >
                      {minuteValue}
                    </button>
                  );
                })}
                <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center text-secondary opacity-80">
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </label>
  );
};

const ColorInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <label
    className="flex items-center gap-3 text-sm"
    style={{
      color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
    }}
  >
    <span className="w-32">{label}</span>
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-10 w-14 rounded border bg-transparent"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--input-background)",
      }}
    />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 h-10 rounded border px-3 text-sm"
      style={{
        borderColor: "var(--border)",
        backgroundColor: "var(--input-background)",
        color: "var(--foreground)",
      }}
    />
  </label>
);

/**
 * Appearance controls for theme mode, preset selection, and custom palettes.
 */
export function AppearanceSettings() {
  const {
    t,
    language,
    themePreferences,
    activeTheme,
    setThemeMode,
    setThemePreset,
    setSchedule,
    updateCustomTheme,
  } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 82%, transparent)",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 78%, transparent)",
  } as const;
  const hoverBorder = "color-mix(in srgb, var(--primary) 40%, var(--border))";
  const hoverShadow =
    "color-mix(in srgb, var(--primary) 24%, var(--background))";
  const getOptionStyle = (active: boolean) =>
    ({
      borderColor: "var(--appearance-border)",
      background: active ? "var(--primary-soft)" : "var(--input-background)",
      boxShadow: "0 0 0 3px var(--appearance-shadow)",
      ...(active
        ? {
            "--appearance-border": "var(--primary)",
            "--appearance-shadow": "var(--focus-ring-soft)",
          }
        : {}),
      "--appearance-border-hover": hoverBorder,
      "--appearance-shadow-hover": hoverShadow,
    }) as CSSProperties;
  const getAccentHoverStyle = (active: boolean) =>
    ({
      borderColor: "var(--appearance-border)",
      borderStyle: active ? "solid" : "dashed",
      background: active ? "var(--primary-soft)" : "var(--input-background)",
      boxShadow: "0 0 0 3px var(--appearance-shadow)",
      ...(active
        ? {
            "--appearance-border": "var(--primary)",
            "--appearance-shadow": "var(--focus-ring)",
          }
        : {}),
      "--appearance-border-hover": hoverBorder,
      "--appearance-shadow-hover": hoverShadow,
    }) as CSSProperties;
  const [customTone, setCustomTone] = useState<Tone>("light");
  const pendingCustomUpdateRef = useRef<{
    light: Partial<ThemePalette>;
    dark: Partial<ThemePalette>;
  }>({ light: {}, dark: {} });
  const rafIdRef = useRef<number | null>(null);
  const customNameRef = useRef(themePreferences.customTheme.name);

  useEffect(() => {
    customNameRef.current = themePreferences.customTheme.name;
  }, [themePreferences.customTheme.name]);

  const flushPendingCustomUpdate = useCallback(() => {
    const pending = pendingCustomUpdateRef.current;
    pendingCustomUpdateRef.current = { light: {}, dark: {} };
    if (Object.keys(pending.light).length > 0) {
      updateCustomTheme("light", pending.light, customNameRef.current);
    }
    if (Object.keys(pending.dark).length > 0) {
      updateCustomTheme("dark", pending.dark, customNameRef.current);
    }
  }, [updateCustomTheme]);

  const scheduleCustomUpdate = useCallback(
    (tone: Tone, palette: Partial<ThemePalette>) => {
      pendingCustomUpdateRef.current = {
        ...pendingCustomUpdateRef.current,
        [tone]: { ...pendingCustomUpdateRef.current[tone], ...palette },
      };
      if (rafIdRef.current !== null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        flushPendingCustomUpdate();
      });
    },
    [flushPendingCustomUpdate]
  );

  useEffect(
    () => () => {
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      flushPendingCustomUpdate();
    },
    [flushPendingCustomUpdate]
  );

  const formatTemplate = (template: string, values: Record<string, string>) =>
    Object.entries(values).reduce(
      (result, [key, value]) => result.split(`{${key}}`).join(value),
      template
    );

  const getPresetLabel = (presetId: string, fallback: string) => {
    const key = `appearance.presets.${presetId}.name`;
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const getRadixSwatchLabel = (name: string) => {
    const key = `appearance.radixSwatches.${name}`;
    const translated = t(key);
    return translated === key ? name : translated;
  };

  const customPalette =
    customTone === "dark"
      ? themePreferences.customTheme.dark
      : themePreferences.customTheme.light;

  const handleSwatchPreset = (tone: Tone, presetId: string) => {
    setThemePreset(tone, presetId);
    if (tone === "dark") {
      setThemeMode("dark");
    } else if (themePreferences.mode === "dark") {
      setThemeMode("light");
    }
  };

  const applySwatchColors = (tone: Tone, colors: string[]) => {
    const targetPalette =
      tone === "dark"
        ? themePreferences.customTheme.dark
        : themePreferences.customTheme.light;
    updateCustomTheme(tone, {
      background: colors[0] ?? targetPalette.background,
      surface: colors[1] ?? targetPalette.surface,
      foreground: colors[2] ?? targetPalette.foreground,
      primary: colors[3] ?? targetPalette.primary,
      accent: colors[4] ?? targetPalette.accent,
    });
    setThemePreset(tone, "custom");
  };

  const renderPresetSection = (tone: Tone) => {
    const presets = tone === "dark" ? DARK_THEME_PRESETS : LIGHT_THEME_PRESETS;
    const activeId =
      tone === "dark" ? themePreferences.darkTheme : themePreferences.lightTheme;
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {presets.map((preset) => (
          <SwatchCard
            key={preset.id}
            palette={preset.palette}
            label={getPresetLabel(preset.id, preset.name)}
            focusLabel={t(
              tone === "dark"
                ? "appearance.darkFocusLabel"
                : "appearance.lightFocusLabel"
            )}
            active={activeId === preset.id}
            onSelect={() => handleSwatchPreset(tone, preset.id)}
            activeLabel={t("appearance.activeLabel")}
          />
        ))}
        <button
          onClick={() => handleSwatchPreset(tone, "custom")}
          className="border rounded-xl flex flex-col gap-3 items-start p-3 justify-center text-left transition-all [--appearance-border:transparent] [--appearance-shadow:transparent] hover:[--appearance-border:var(--appearance-border-hover)] hover:[--appearance-shadow:var(--appearance-shadow-hover)]"
          style={getAccentHoverStyle(activeId === "custom")}
        >
          <Sparkles className="w-5 h-5" style={iconStyle} />
          <div>
            <p className="font-medium" style={primaryTextStyle}>
              {t("appearance.customTitle")}
            </p>
            <p className="text-xs" style={secondaryTextStyle}>
              {t("appearance.customDescription")}
            </p>
          </div>
        </button>
      </div>
    );
  };

  const formatTime = (date: Date) =>
    new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);

  const sunriseSunsetInfo = useMemo(() => {
    // Privacy-friendly: use fixed Passau coordinates (no geolocation, no network).
    const LAT = 48.5667;
    const LON = 13.4319;

    const rad = Math.PI / 180;
    const J1970 = 2440588;
    const J2000 = 2451545;
    const dayMs = 86400000;
    const degToRad = (deg: number) => deg * rad;

    const toJulian = (date: Date) => date.valueOf() / dayMs - 0.5 + J1970;
    const fromJulian = (j: number) => new Date((j + 0.5 - J1970) * dayMs);
    const toDays = (date: Date) => toJulian(date) - J2000;

    const solarMeanAnomaly = (d: number) =>
      degToRad(357.5291 + 0.98560028 * d);
    const eclipticLongitude = (M: number) => {
      const C =
        degToRad(1.9148) * Math.sin(M) +
        degToRad(0.0200) * Math.sin(2 * M) +
        degToRad(0.0003) * Math.sin(3 * M);
      const P = degToRad(102.9372);
      return M + C + P + Math.PI;
    };
    const declination = (L: number, b = 0) =>
      Math.asin(
        Math.sin(b) * Math.cos(degToRad(23.4397)) +
          Math.cos(b) * Math.sin(degToRad(23.4397)) * Math.sin(L)
      );
    const solarTransitJ = (ds: number, M: number, L: number) =>
      J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
    const hourAngle = (h: number, phi: number, d: number) =>
      Math.acos(
        (Math.sin(h) - Math.sin(phi) * Math.sin(d)) /
          (Math.cos(phi) * Math.cos(d))
      );

    const getTimes = (date: Date) => {
      const lw = -degToRad(LON);
      const phi = degToRad(LAT);
      const d = toDays(date);
      const n = Math.round(d - lw / (2 * Math.PI));
      const ds = n + lw / (2 * Math.PI);
      const M = solarMeanAnomaly(ds);
      const L = eclipticLongitude(M);
      const dec = declination(L);
      const h0 = degToRad(-0.833);
      const w = hourAngle(h0, phi, dec);
      const Jtransit = solarTransitJ(ds, M, L);
      const Jrise = Jtransit - w / (2 * Math.PI);
      const Jset = Jtransit + w / (2 * Math.PI);
      return { sunrise: fromJulian(Jrise), sunset: fromJulian(Jset) };
    };

    const now = new Date();
    const { sunrise, sunset } = getTimes(now);
    const isDay = now >= sunrise && now < sunset;
    const nextSwitch =
      now < sunrise
        ? sunrise
        : now < sunset
        ? sunset
        : (() => {
            const tomorrow = new Date(now);
            tomorrow.setDate(now.getDate() + 1);
            return getTimes(tomorrow).sunrise;
          })();

    return {
      sunrise: formatTime(sunrise),
      sunset: formatTime(sunset),
      nextSwitch: formatTime(nextSwitch),
      isDay,
    };
  }, [language]);

  const modeOptions = useMemo(
    () => [
      {
        value: "light" as ThemeMode,
        label: t("appearance.modeLight"),
        icon: <Sun className="w-4 h-4" />,
        description: t("appearance.modeLightLabel"),
      },
      {
        value: "dark" as ThemeMode,
        label: t("appearance.modeDark"),
        icon: <Moon className="w-4 h-4" />,
        description: t("appearance.modeDarkLabel"),
      },
      {
        value: "system" as ThemeMode,
        label: t("appearance.modeSystem"),
        icon: <Laptop className="w-4 h-4" />,
        description: t("appearance.modeSystemLabel"),
      },
      {
        value: "sunrise-sunset" as ThemeMode,
        label: t("appearance.modeSun"),
        icon: sunriseSunsetInfo.isDay ? (
          <Sunrise className="w-4 h-4" />
        ) : (
          <Sunset className="w-4 h-4" />
        ),
        description: t("appearance.modeSunLabel"),
      },
      {
        value: "scheduled" as ThemeMode,
        label: t("appearance.modeSchedule"),
        icon: <Sparkles className="w-4 h-4" />,
        description: t("appearance.modeScheduleLabel"),
      },
    ],
    [t, sunriseSunsetInfo.isDay]
  );

  return (
    <Card className="stat-card">
      <CardHeader className="flex flex-col gap-2">
        <CardTitle className="flex items-center gap-2 text-primary-heading font-semibold">
          <Palette className="w-5 h-5" style={iconStyle} />
          {t("appearance.title")}
          <Badge variant="outline">
            {activeTheme.tone === "dark"
              ? t("appearance.darkMode")
              : t("appearance.lightMode")}
          </Badge>
        </CardTitle>
        <CardDescription className="card-description-text" style={primaryTextStyle}>
          {t("appearance.subtitle")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <p className="text-sm font-medium" style={primaryTextStyle}>
            {t("appearance.modeLabel")}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setThemeMode(option.value)}
                className="flex items-start gap-3 rounded-lg border p-3 text-left transition [--appearance-border:transparent] [--appearance-shadow:transparent] hover:[--appearance-border:var(--appearance-border-hover)] hover:[--appearance-shadow:var(--appearance-shadow-hover)]"
                style={getOptionStyle(themePreferences.mode === option.value)}
              >
                <div
                  className="p-2 rounded-md border"
                  style={{
                    background: "var(--input-background)",
                    borderColor: "var(--border)",
                    color: iconStyle.color,
                  }}
                >
                  {option.icon}
                </div>
                <div>
                  <p className="text-sm font-medium" style={primaryTextStyle}>
                    {option.label}
                  </p>
                  <p className="text-xs" style={secondaryTextStyle}>
                    {option.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {themePreferences.mode === "sunrise-sunset" && (
          <div
            className="rounded-lg border p-3"
            style={{
              borderColor: "var(--border)",
              background: "var(--primary-soft-weak)",
            }}
          >
            <p className="text-sm font-medium text-[var(--foreground)] mb-1">
              {t("appearance.sunriseTitle")}
            </p>
            <p className="text-xs text-secondary">
              {formatTemplate(t("appearance.sunriseDescription"), {
                sunrise: sunriseSunsetInfo.sunrise,
                sunset: sunriseSunsetInfo.sunset,
                state: sunriseSunsetInfo.isDay
                  ? t("appearance.sunriseStateDay")
                  : t("appearance.sunriseStateNight"),
                nextSwitch: sunriseSunsetInfo.nextSwitch,
              })}
            </p>
          </div>
        )}

        {themePreferences.mode === "scheduled" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TimeSlider
              label={t("appearance.scheduleLight")}
              hourLabel={t("appearance.scheduleHour")}
              minuteLabel={t("appearance.scheduleMinute")}
              value={themePreferences.scheduledLightTime}
              onChange={(value) =>
                setSchedule(value, themePreferences.scheduledDarkTime)
              }
            />
            <TimeSlider
              label={t("appearance.scheduleDark")}
              hourLabel={t("appearance.scheduleHour")}
              minuteLabel={t("appearance.scheduleMinute")}
              value={themePreferences.scheduledDarkTime}
              onChange={(value) =>
                setSchedule(themePreferences.scheduledLightTime, value)
              }
            />
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Sun className="w-4 h-4" style={iconStyle} />
            <p className="font-medium" style={primaryTextStyle}>
              {t("appearance.lightThemes")}
            </p>
          </div>
          {renderPresetSection("light")}
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Moon className="w-4 h-4" style={iconStyle} />
            <p className="font-medium" style={primaryTextStyle}>
              {t("appearance.darkThemes")}
            </p>
          </div>
          {renderPresetSection("dark")}
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles
                className="w-4 h-4"
                style={iconStyle}
              />
              <p className="font-medium" style={primaryTextStyle}>
                {t("appearance.customBuilder")}
              </p>
            </div>
            <Tabs
              value={customTone}
              onValueChange={(value) => setCustomTone(value as Tone)}
            >
              <TabsList>
                <TabsTrigger value="light">{t("appearance.lightMode")}</TabsTrigger>
                <TabsTrigger value="dark">{t("appearance.darkMode")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ColorInput
              label={t("appearance.colorBackground")}
              value={customPalette.background}
              onChange={(value) =>
                scheduleCustomUpdate(customTone, { background: value })
              }
            />
            <ColorInput
              label={t("appearance.colorSurface")}
              value={customPalette.surface}
              onChange={(value) =>
                scheduleCustomUpdate(customTone, { surface: value })
              }
            />
            <ColorInput
              label={t("appearance.colorForeground")}
              value={customPalette.foreground}
              onChange={(value) =>
                scheduleCustomUpdate(customTone, { foreground: value })
              }
            />
            <ColorInput
              label={t("appearance.colorPrimary")}
              value={customPalette.primary}
              onChange={(value) =>
                scheduleCustomUpdate(customTone, { primary: value })
              }
            />
            <ColorInput
              label={t("appearance.colorAccent")}
              value={customPalette.accent}
              onChange={(value) =>
                scheduleCustomUpdate(customTone, { accent: value })
              }
            />
          </div>

          <div className="space-y-2">
            <p className="text-xs" style={secondaryTextStyle}>
              {t("appearance.radixPaletteHint")}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(RADIX_SWATCHES).map(([name, swatch]) => (
                <button
                  key={name}
                  onClick={() => applySwatchColors(customTone, swatch)}
                  className="flex items-center gap-2 rounded border p-2 [--appearance-border:transparent] [--appearance-shadow:transparent] hover:[--appearance-border:var(--appearance-border-hover)] hover:shadow-[0_0_0_3px_var(--appearance-shadow)]"
                  style={
                    {
                      borderColor: "var(--appearance-border)",
                      "--appearance-border-hover": hoverBorder,
                      "--appearance-shadow": hoverShadow,
                    } as CSSProperties
                  }
                >
                  <div className="flex gap-1">
                    {swatch.map((color) => (
                      <span
                        key={color}
                        className="h-6 w-4 rounded-sm"
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <span className="text-xs capitalize" style={secondaryTextStyle}>
                    {getRadixSwatchLabel(name)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
